import { doc, setDoc, getDoc, onSnapshot, updateDoc, increment, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, logFeatureUsage } from './firebaseService';
import { getDateLocale } from './localeHelpers';
import { SpotVisit, Badge, UserTier, CampingSpot } from '../types';
import { VISIT_BADGES, USER_TIERS } from '../constants';
import { sendLevelUpInboxMessage } from './userMessageService';
import { unwrapSyntheticSpotId } from './waypointService';
import { writeSpotVehicleVisit, deleteSpotVehicleVisit, nightsBetween } from './spotVehicleVisitService';

/**
 * Bidragsscore = unike besøkte plasser + antall verifiserte plasser.
 * Å verifisere en plass (anmeldelse/bilde) teller dermed EKSTRA mot nivået —
 * i praksis dobbelt, siden du må ha besøkt en plass for å verifisere den.
 * `verifiedCount` er valgfri og defaulter til 0, så eksisterende kallere
 * (som kun bryr seg om besøk) er upåvirket og ingen bruker kan falle i nivå.
 */
export function contributionScore(visits: SpotVisit[], verifiedCount = 0): number {
  const uniqueSpots = new Set(visits.map(v => v.spotId)).size;
  return uniqueSpots + Math.max(0, verifiedCount);
}

export function getUserTier(visits: SpotVisit[], verifiedCount = 0): UserTier {
  const score = contributionScore(visits, verifiedCount);
  // USER_TIERS is sorted by level, we find the highest tier where threshold <= score
  const tier = [...USER_TIERS].reverse().find(t => score >= t.threshold);
  return tier || USER_TIERS[0]; // Default to level 1
}

export function getNextTier(visits: SpotVisit[], verifiedCount = 0): UserTier | null {
  const score = contributionScore(visits, verifiedCount);
  return USER_TIERS.find(t => t.threshold > score) || null;
}

export interface VisitProgressResult {
  levelUp: boolean;
  newTier?: UserTier;
  currentTier: UserTier;
  nextTier: UserTier | null;
  uniqueSpots: number;
  spotsToNextTier: number;
  wasAdded: boolean;
}

/**
 * One-shot cleanup: fjern besøk med wpcard-/visit_-prefix i spotId
 * (akkumulert fra en tidligere bug). Dedupliserer også slik at hver
 * unwrap-id kun finnes én gang. Returnerer antall fjernet/skrevet,
 * eller null hvis ingen aksjon var nødvendig.
 */
export async function cleanupCorruptedVisits(userId: string): Promise<{ removed: number; deduped: number } | null> {
  const ref = doc(db, 'user_visits', userId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const visits = (snap.data()?.visits || []) as SpotVisit[];
  if (!Array.isArray(visits) || visits.length === 0) return null;

  const corrupted = visits.filter(v => typeof v.spotId === 'string' &&
    (v.spotId.startsWith('wpcard-') || v.spotId.startsWith('visit_')));
  if (corrupted.length === 0) return null;

  // Beholder kun unwrappede unike spotIds; bevarer eldste visitedAt for hver
  const byId = new Map<string, SpotVisit>();
  for (const v of visits) {
    const cleanId = unwrapSyntheticSpotId(v.spotId);
    if (!cleanId || cleanId.startsWith('wpcard-') || cleanId.startsWith('visit_')) continue;
    const existing = byId.get(cleanId);
    if (!existing || (v.visitedAt || 0) < (existing.visitedAt || 0)) {
      byId.set(cleanId, { ...v, spotId: cleanId });
    }
  }
  const cleaned = Array.from(byId.values());
  await setDoc(ref, { visits: cleaned }, { merge: true });
  console.log('[VISIT-CLEANUP] removed', corrupted.length, 'corrupted visits, deduped to', cleaned.length, 'unique spotIds');
  return { removed: corrupted.length, deduped: cleaned.length };
}

export async function toggleVisitSpot(
  spotId: string,
  spotName: string,
  latitude: number,
  longitude: number,
  userId: string,
  currentVisits: SpotVisit[],
  county?: string,
  photoUrl?: string,
  vehicleCategory?: SpotVisit['vehicleCategory'],
  vehicleLength?: number,
  verifiedCount = 0,
  garagePhotoUrl?: string | null,
  shareVehicleOnSpots = true,
): Promise<VisitProgressResult> {
  // KRITISK: strip wpcard-/visit_-prefixer fra spot-id før vi lagrer som visit.
  // Hvis brukeren klikker på en syntetisk wpcard- spot, vil spot.id være
  // `wpcard-visit_<originalId>`. Lagrer vi det som spotId, lager bridge-koden
  // i useMyContent en ny `visit_wpcard-...`-id, som synthesiseres til
  // `wpcard-visit_wpcard-...` neste runde — uendelig løkke.
  spotId = unwrapSyntheticSpotId(spotId);
  const ref = doc(db, 'user_visits', userId);
  const alreadyVisited = currentVisits.some(v => v.spotId === spotId);

  const oldTier = getUserTier(currentVisits, verifiedCount);
  let updatedVisits: SpotVisit[] = [];

  if (alreadyVisited) {
    updatedVisits = currentVisits.filter(v => v.spotId !== spotId);
  } else {
    const newVisit: SpotVisit = {
      spotId,
      spotName,
      visitedAt: Date.now(),
      latitude,
      longitude,
      ...(county ? { county } : {}),
      ...(photoUrl ? { photoUrl } : {}),
      ...(vehicleCategory ? { vehicleCategory } : {}),
      ...(vehicleLength ? { vehicleLength } : {}),
    };
    updatedVisits = [...currentVisits, newVisit];
  }

  await setDoc(ref, { visits: updatedVisits });

  // Atomically update visitCount + vehicle aggregation on the spot document
  const spotRef = doc(db, 'cached_spots', spotId);
  const spotUpdate: Record<string, any> = { visitCount: increment(alreadyVisited ? -1 : 1) };

  if (alreadyVisited) {
    // Un-visiting: decrement the old visit's vehicle category
    const oldVisit = currentVisits.find(v => v.spotId === spotId);
    if (oldVisit?.vehicleCategory) {
      spotUpdate[`vehicleVisits.${oldVisit.vehicleCategory}`] = increment(-1);
    }
  } else {
    // Visiting: increment vehicle category count + update max length
    if (vehicleCategory) {
      spotUpdate[`vehicleVisits.${vehicleCategory}`] = increment(1);
    }
    if (vehicleLength) {
      try {
        const spotSnap = await getDoc(spotRef);
        const currentMax = spotSnap.data()?.vehicleMaxLength ?? 0;
        if (vehicleLength > currentMax) {
          spotUpdate.vehicleMaxLength = vehicleLength;
        }
      } catch { /* ignore — maxLength update is best-effort */ }
    }
  }

  await updateDoc(spotRef, spotUpdate).catch(() => {});

  const newTier = getUserTier(updatedVisits, verifiedCount);
  const levelUp = !alreadyVisited && newTier.level > oldTier.level;

  // Persist tier on user document for UGC display (e.g. badges next to username)
  if (!alreadyVisited) {
    logFeatureUsage('spot_visit');
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      tier: newTier.level,
      totalVisits: new Set(updatedVisits.map(v => v.spotId)).size
    }, { merge: true });
  }

  if (levelUp && newTier) {
    try {
      await sendLevelUpInboxMessage(userId, newTier.name, newTier.level);
    } catch (err) {
      console.error('Failed to send level up inbox message', err);
    }
  }

  const nextTier = getNextTier(updatedVisits, verifiedCount);
  const uniqueSpots = new Set(updatedVisits.map(v => v.spotId)).size;
  const score = contributionScore(updatedVisits, verifiedCount);

  return {
    levelUp,
    newTier: levelUp ? newTier : undefined,
    currentTier: newTier,
    nextTier,
    uniqueSpots,
    spotsToNextTier: nextTier ? Math.max(0, nextTier.threshold - score) : 0,
    wasAdded: !alreadyVisited,
  };
}

/**
 * Deterministisk, Firestore-trygg dok-ID for ett opphold: plass + startdato.
 * Firestore-IDer kan ikke inneholde '/', så vi saniterer. Samme (plass, checkInAt)
 * gir alltid samme ID → skriving er idempotent (retry/backfill overskriver samme rad).
 */
export function checkinLogDocId(spotId: string, checkInAt: number): string {
  return `${spotId.replace(/\//g, '_')}_${checkInAt}`;
}

/**
 * Skriv/oppdater en append-only opphold-oppføring i `users/{uid}/checkin_log`.
 *
 *   ┌─ visits-settet (aggregat, badge-kilde) ── beholdes ──┐
 *   │                                                       │
 *   └─► checkin_log/{spotId}_{checkInAt}  (granulær sannhet for enkeltopphold)
 *
 * Fase 1 speiler visits-settets spenn (ett opphold per plass); Fase 2 innfører flere
 * atskilte opphold på samme plass. Deterministisk ID → idempotent. Se
 * `docs/plan-checkin-log-fase1.md`. Best-effort: feiler den, reparerer neste
 * idempotente backfill-kjøring raden (loggen er utledbar fra settet for retro).
 */
export async function writeCheckinLogEntry(
  userId: string,
  entry: {
    spotId: string; spotName: string; latitude: number; longitude: number;
    checkInAt: number; checkedOutAt?: number; county?: string; photoUrl?: string;
    vehicleCategory?: SpotVisit['vehicleCategory']; vehicleLength?: number;
    source: 'live' | 'retro';
  },
): Promise<void> {
  const ref = doc(db, 'users', userId, 'checkin_log', checkinLogDocId(entry.spotId, entry.checkInAt));
  await setDoc(ref, {
    spotId: entry.spotId,
    spotName: entry.spotName,
    latitude: entry.latitude,
    longitude: entry.longitude,
    checkInAt: entry.checkInAt,
    source: entry.source,
    ...(entry.checkedOutAt ? { checkedOutAt: entry.checkedOutAt } : {}),
    ...(entry.county ? { county: entry.county } : {}),
    ...(entry.photoUrl ? { photoUrl: entry.photoUrl } : {}),
    ...(entry.vehicleCategory ? { vehicleCategory: entry.vehicleCategory } : {}),
    ...(entry.vehicleLength ? { vehicleLength: entry.vehicleLength } : {}),
  }, { merge: true });
}

/**
 * Etterregistrer et besøk med en valgt (typisk tilbakedatert) dato — for plasser
 * brukeren har vært på men glemte å sjekke inn. I motsetning til `toggleVisitSpot`
 * TOGGLER denne aldri av: finnes ikke plassen legges den til med `visitedAt`; finnes
 * den, oppdateres kun datoen (rett dato) — besøket fjernes aldri ved uhell.
 *
 * `source: 'retro'` markerer at besøket er selvrapportert (ikke geo-verifisert), slik
 * at server-triggere kan dempe eier-varsler for gamle besøk. Badges avgjøres av antall
 * unike plasser (dato irrelevant), mens reisetavle/reisefilm sorterer på `visitedAt`,
 * så et tilbakedatert besøk glir automatisk inn på riktig sted i tidslinjen.
 */
export async function addPastVisit(
  spotId: string,
  spotName: string,
  latitude: number,
  longitude: number,
  userId: string,
  currentVisits: SpotVisit[],
  visitedAt: number,
  county?: string,
  photoUrl?: string,
  vehicleCategory?: SpotVisit['vehicleCategory'],
  vehicleLength?: number,
  verifiedCount = 0,
  garagePhotoUrl?: string | null,
  shareVehicleOnSpots = true,
): Promise<VisitProgressResult> {
  spotId = unwrapSyntheticSpotId(spotId);
  const ref = doc(db, 'user_visits', userId);
  const existingIdx = currentVisits.findIndex(v => v.spotId === spotId);
  const alreadyVisited = existingIdx !== -1;

  const oldTier = getUserTier(currentVisits, verifiedCount);
  let updatedVisits: SpotVisit[];

  if (alreadyVisited) {
    // Flere innsjekker etter hverandre på samme plass = ETT opphold over flere dager.
    // Vi utvider spennet i stedet for å overskrive: visitedAt = tidligste dato,
    // checkedOutAt = seneste. Reisefilm/reisetavle viser da «3.–5. juli» som ett stopp.
    // (Settmodellen holder kun ett opphold per plass; to atskilte turer til samme plass
    //  smelter dermed sammen — kjent begrensning som checkin_log senere løser.)
    const existing = currentVisits[existingIdx];
    const start = Math.min(existing.visitedAt, visitedAt);
    const priorEnd = existing.checkedOutAt ?? existing.visitedAt;
    const end = Math.max(priorEnd, visitedAt);
    const spanned = end > start ? { checkedOutAt: end } : {};
    updatedVisits = currentVisits.map((v, i) =>
      i === existingIdx ? { ...v, visitedAt: start, ...spanned, source: 'retro' as const } : v
    );
  } else {
    const newVisit: SpotVisit = {
      spotId,
      spotName,
      visitedAt,
      source: 'retro',
      latitude,
      longitude,
      ...(county ? { county } : {}),
      ...(photoUrl ? { photoUrl } : {}),
      ...(vehicleCategory ? { vehicleCategory } : {}),
      ...(vehicleLength ? { vehicleLength } : {}),
    };
    updatedVisits = [...currentVisits, newVisit];
  }

  await setDoc(ref, { visits: updatedVisits });

  // Fase 1 checkin_log: speil oppholdet i den append-only loggen (granulær sannhet
  // for reisefilm v1.1). Flyttes startdatoen (spenn utvidet bakover), slett den gamle
  // raden så den ikke blir foreldreløs. Best-effort — idempotent backfill reparerer.
  const written = updatedVisits.find(v => v.spotId === spotId);
  if (written) {
    try {
      if (alreadyVisited) {
        const oldStart = currentVisits[existingIdx].visitedAt;
        if (oldStart !== written.visitedAt) {
          await deleteDoc(doc(db, 'users', userId, 'checkin_log', checkinLogDocId(spotId, oldStart)));
        }
      }
      await writeCheckinLogEntry(userId, {
        spotId,
        spotName: written.spotName,
        latitude: written.latitude,
        longitude: written.longitude,
        checkInAt: written.visitedAt,
        checkedOutAt: written.checkedOutAt,
        county: written.county,
        photoUrl: written.photoUrl,
        vehicleCategory: written.vehicleCategory,
        vehicleLength: written.vehicleLength,
        source: 'retro',
      });
    } catch (err) {
      console.error('checkin_log-skriving feilet (selvhelbredes av backfill)', err);
    }
  }

  if (written && !alreadyVisited) {
    await writeSpotVehicleVisit({
      userId,
      spotId,
      checkInAt: written.visitedAt,
      vehicleCategory: written.vehicleCategory,
      vehicleLengthCm: written.vehicleLength,
      photoUrl: garagePhotoUrl,
      nights: nightsBetween(written.visitedAt, written.checkedOutAt),
      source: 'retro',
      shareOnSpots: shareVehicleOnSpots,
    }).catch(() => {});
  }

  // Inkrementer spott-aggregater KUN ved en ekte ny plass. Dato-retting på et
  // eksisterende besøk skal ikke røre visitCount/kjøretøy-tellere.
  if (!alreadyVisited) {
    const spotRef = doc(db, 'cached_spots', spotId);
    const spotUpdate: Record<string, any> = { visitCount: increment(1) };
    if (vehicleCategory) {
      spotUpdate[`vehicleVisits.${vehicleCategory}`] = increment(1);
    }
    if (vehicleLength) {
      try {
        const spotSnap = await getDoc(spotRef);
        const currentMax = spotSnap.data()?.vehicleMaxLength ?? 0;
        if (vehicleLength > currentMax) {
          spotUpdate.vehicleMaxLength = vehicleLength;
        }
      } catch { /* ignore — maxLength update is best-effort */ }
    }
    await updateDoc(spotRef, spotUpdate).catch(() => {});
  }

  const newTier = getUserTier(updatedVisits, verifiedCount);
  const levelUp = !alreadyVisited && newTier.level > oldTier.level;

  if (!alreadyVisited) {
    logFeatureUsage('spot_visit_retro');
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      tier: newTier.level,
      totalVisits: new Set(updatedVisits.map(v => v.spotId)).size
    }, { merge: true });
  }

  if (levelUp && newTier) {
    try {
      await sendLevelUpInboxMessage(userId, newTier.name, newTier.level);
    } catch (err) {
      console.error('Failed to send level up inbox message', err);
    }
  }

  const nextTier = getNextTier(updatedVisits, verifiedCount);
  const uniqueSpots = new Set(updatedVisits.map(v => v.spotId)).size;
  const score = contributionScore(updatedVisits, verifiedCount);

  return {
    levelUp,
    newTier: levelUp ? newTier : undefined,
    currentTier: newTier,
    nextTier,
    uniqueSpots,
    spotsToNextTier: nextTier ? Math.max(0, nextTier.threshold - score) : 0,
    wasAdded: !alreadyVisited,
  };
}

/** Ett opphold fra checkin_log (Fase 2: flere atskilte opphold per plass). */
export interface CheckinStay {
  id: string;
  spotId: string;
  spotName: string;
  latitude: number;
  longitude: number;
  checkInAt: number;
  checkedOutAt?: number;
  county?: string;
  photoUrl?: string;
  vehicleCategory?: SpotVisit['vehicleCategory'];
  vehicleLength?: number;
  source?: 'live' | 'retro';
}

/** Hent alle opphold for én plass fra checkin_log, sortert kronologisk på start. */
export async function getCheckinStays(userId: string, spotId: string): Promise<CheckinStay[]> {
  spotId = unwrapSyntheticSpotId(spotId);
  const q = query(collection(db, 'users', userId, 'checkin_log'), where('spotId', '==', spotId));
  const snap = await getDocs(q);
  const stays = snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<CheckinStay, 'id'>) }));
  return stays.sort((a, b) => a.checkInAt - b.checkInAt);
}

/**
 * Fase 3-datakilde for reisefilm/reisetavle-periode: hele checkin_log-en som
 * `SpotVisit[]` — ÉN oppføring per opphold (`visitedAt = checkInAt`), så atskilte
 * gjenbesøk på samme plass blir separate stopp i filmen. Er loggen tom (brukeren har
 * ingen logg-data ennå), faller vi tilbake til `fallback` (visits-settet) — så ingen
 * migrering kreves; historikk vokser inn. Feiler lesningen, bruk fallback.
 */
export async function getReplayVisits(userId: string, fallback: SpotVisit[]): Promise<SpotVisit[]> {
  try {
    const snap = await getDocs(collection(db, 'users', userId, 'checkin_log'));
    if (snap.empty) return fallback;
    return snap.docs.map(d => {
      const s = d.data() as Omit<CheckinStay, 'id'>;
      return {
        spotId: s.spotId,
        spotName: s.spotName,
        visitedAt: s.checkInAt,
        latitude: s.latitude,
        longitude: s.longitude,
        ...(typeof s.checkedOutAt === 'number' ? { checkedOutAt: s.checkedOutAt } : {}),
        ...(s.county ? { county: s.county } : {}),
        ...(s.photoUrl ? { photoUrl: s.photoUrl } : {}),
        ...(s.vehicleCategory ? { vehicleCategory: s.vehicleCategory } : {}),
        ...(typeof s.vehicleLength === 'number' ? { vehicleLength: s.vehicleLength } : {}),
      } as SpotVisit;
    });
  } catch {
    return fallback;
  }
}

/**
 * Re-utled visits-settets aggregat for én plass FRA checkin_log. Settet holder ett
 * element per plass, så det blir konvolutten av alle opphold:
 *
 *   visitedAt   = tidligste checkInAt   (min)
 *   checkedOutAt = seneste checkedOutAt (max)   ← utelates hvis ett-dags/ingen slutt
 *
 * Ingen opphold igjen → plassen fjernes fra settet (og visitCount -1). Ny plass i
 * settet → visitCount +1. Badges/tier teller unike plasser (uendret). Dette gjør settet
 * til et ekte avledet aggregat — loggen er sannhetskilden. Se docs/plan-checkin-log-fase1.md.
 */
async function recomputeVisitAggregate(
  userId: string,
  spotId: string,
  verifiedCount: number,
): Promise<VisitProgressResult> {
  spotId = unwrapSyntheticSpotId(spotId);
  // Les settet LIVE fra Firestore (ikke en passert closure) — unngår race ved raske
  // gjentatte endringer der en stale `currentVisits` ville dobbelttelle visitCount.
  const [stays, visitsSnap] = await Promise.all([
    getCheckinStays(userId, spotId),
    getDoc(doc(db, 'user_visits', userId)),
  ]);
  const currentVisits: SpotVisit[] = (visitsSnap.exists() ? (visitsSnap.data()?.visits as SpotVisit[]) : []) || [];
  const wasInSet = currentVisits.some(v => v.spotId === spotId);
  const oldTier = getUserTier(currentVisits, verifiedCount);

  let updatedVisits: SpotVisit[];
  if (stays.length === 0) {
    updatedVisits = currentVisits.filter(v => v.spotId !== spotId);
  } else {
    const start = Math.min(...stays.map(s => s.checkInAt));
    const end = Math.max(...stays.map(s => s.checkedOutAt ?? s.checkInAt));
    const latest = stays[stays.length - 1]; // nyeste opphold vinner for denormaliserte felt
    const base = currentVisits.find(v => v.spotId === spotId);
    const entry: SpotVisit = {
      spotId,
      spotName: latest.spotName || base?.spotName || '',
      visitedAt: start,
      latitude: latest.latitude ?? base?.latitude ?? 0,
      longitude: latest.longitude ?? base?.longitude ?? 0,
      source: 'retro',
      ...(end > start ? { checkedOutAt: end } : {}),
      ...((latest.county || base?.county) ? { county: latest.county || base?.county } : {}),
      ...((latest.photoUrl || base?.photoUrl) ? { photoUrl: latest.photoUrl || base?.photoUrl } : {}),
      ...(latest.vehicleCategory ? { vehicleCategory: latest.vehicleCategory } : {}),
      ...(latest.vehicleLength ? { vehicleLength: latest.vehicleLength } : {}),
    };
    updatedVisits = wasInSet
      ? currentVisits.map(v => (v.spotId === spotId ? entry : v))
      : [...currentVisits, entry];
  }

  await setDoc(doc(db, 'user_visits', userId), { visits: updatedVisits });

  // visitCount justeres kun når plassen KOMMER INN i eller FORSVINNER fra settet.
  const nowInSet = updatedVisits.some(v => v.spotId === spotId);
  if (nowInSet !== wasInSet) {
    await updateDoc(doc(db, 'cached_spots', spotId), {
      visitCount: increment(nowInSet ? 1 : -1),
    }).catch(() => {});
  }

  const newTier = getUserTier(updatedVisits, verifiedCount);
  const levelUp = newTier.level > oldTier.level;
  await setDoc(doc(db, 'users', userId), {
    tier: newTier.level,
    totalVisits: new Set(updatedVisits.map(v => v.spotId)).size,
  }, { merge: true });

  if (levelUp) {
    try { await sendLevelUpInboxMessage(userId, newTier.name, newTier.level); }
    catch (err) { console.error('Failed to send level up inbox message', err); }
  }

  const nextTier = getNextTier(updatedVisits, verifiedCount);
  const uniqueSpots = new Set(updatedVisits.map(v => v.spotId)).size;
  const score = contributionScore(updatedVisits, verifiedCount);
  return {
    levelUp,
    newTier: levelUp ? newTier : undefined,
    currentTier: newTier,
    nextTier,
    uniqueSpots,
    spotsToNextTier: nextTier ? Math.max(0, nextTier.threshold - score) : 0,
    wasAdded: nowInSet && !wasInSet,
  };
}

/**
 * Fase 2: registrer ETT atskilt opphold (fra–til) på en plass. Hvert opphold er sin
 * egen checkin_log-rad ({spotId}_{checkInAt}); flere opphold på samme plass eksisterer
 * side om side (i motsetning til Fase 1 sin spenn-sammenslåing). Settet re-utledes som
 * konvolutt. `checkedOutAt` utelates for ett-dags opphold. Idempotent på (plass, start).
 */
export async function addPastStay(
  spotId: string,
  spotName: string,
  latitude: number,
  longitude: number,
  userId: string,
  checkInAt: number,
  checkedOutAt?: number,
  county?: string,
  photoUrl?: string,
  vehicleCategory?: SpotVisit['vehicleCategory'],
  vehicleLength?: number,
  verifiedCount = 0,
  garagePhotoUrl?: string | null,
  shareVehicleOnSpots = true,
): Promise<VisitProgressResult> {
  spotId = unwrapSyntheticSpotId(spotId);
  await writeCheckinLogEntry(userId, {
    spotId, spotName, latitude, longitude, checkInAt,
    ...(checkedOutAt && checkedOutAt > checkInAt ? { checkedOutAt } : {}),
    county, photoUrl, vehicleCategory, vehicleLength, source: 'retro',
  });
  await writeSpotVehicleVisit({
    userId,
    spotId,
    checkInAt,
    vehicleCategory,
    vehicleLengthCm: vehicleLength,
    photoUrl: garagePhotoUrl,
    nights: nightsBetween(checkInAt, checkedOutAt),
    source: 'retro',
    shareOnSpots: shareVehicleOnSpots,
  }).catch(() => {});
  logFeatureUsage('spot_visit_retro');
  return recomputeVisitAggregate(userId, spotId, verifiedCount);
}

/**
 * Fase 2: slett ETT opphold (identifisert av startdato) og re-utled settet fra de
 * gjenværende oppholdene. Er det siste oppholdet på plassen, forsvinner plassen fra
 * settet (visitCount -1). Idempotent — sletting av en rad som ikke finnes er en no-op.
 */
export async function deletePastStay(
  userId: string,
  spotId: string,
  checkInAt: number,
  verifiedCount = 0,
): Promise<VisitProgressResult> {
  spotId = unwrapSyntheticSpotId(spotId);
  await deleteSpotVehicleVisit(userId, spotId, checkInAt);
  await deleteDoc(doc(db, 'users', userId, 'checkin_log', checkinLogDocId(spotId, checkInAt))).catch(() => {});
  return recomputeVisitAggregate(userId, spotId, verifiedCount);
}

/**
 * Merk utsjekktidspunkt på det seneste besøket for en plass. Besøk lagres som ett
 * element per unike plass (settet med besøkte plasser), så vi setter `checkedOutAt` på
 * den matchende oppføringen. No-op hvis plassen ikke finnes i besøkslista. Grunnlaget
 * for en fremtidig reiserute/blogg (varighet per opphold).
 */
export async function recordCheckOut(
  userId: string,
  spotId: string,
  currentVisits: SpotVisit[],
  checkedOutAt: number = Date.now(),
): Promise<boolean> {
  spotId = unwrapSyntheticSpotId(spotId);
  const idx = currentVisits.findIndex(v => v.spotId === spotId);
  if (idx === -1) return false;
  const updatedVisits = currentVisits.map((v, i) => (i === idx ? { ...v, checkedOutAt } : v));
  await setDoc(doc(db, 'user_visits', userId), { visits: updatedVisits });
  return true;
}

export function subscribeToVisits(
  userId: string,
  callback: (visits: SpotVisit[]) => void
): () => void {
  return onSnapshot(doc(db, 'user_visits', userId), (snap) => {
    callback(snap.exists() ? (snap.data().visits as SpotVisit[]) || [] : []);
  }, () => callback([]));
}

export function getEarnedBadges(visits: SpotVisit[]): Badge[] {
  const uniqueSpots = new Set(visits.map(v => v.spotId)).size;
  return VISIT_BADGES.filter(badge => badge.threshold <= uniqueSpots);
}

export function getNextBadge(visits: SpotVisit[]): Badge | null {
  const uniqueSpots = new Set(visits.map(v => v.spotId)).size;
  return VISIT_BADGES.find(badge => badge.threshold > uniqueSpots) ?? null;
}

export function formatVisitDate(ts: number): string {
  return new Date(ts).toLocaleDateString(getDateLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Auto-tittel for reisefilmen (CapCut-inspirert åpningstekst): beregnes fra
 * reisens tidsspenn. Samme måned → «Juli 2026»; sommermånedene i samme år →
 * «Sommeren 2026» (i18n-tekst injiseres av kalleren); ellers datointervall.
 * Locale injiserbar for deterministiske tester.
 */
export function formatTripTitle(
  firstMs: number,
  lastMs: number,
  summerLabel?: string, // ferdig oversatt «Sommeren {{year}}»-tekst, valgfri
  locale: string = getDateLocale(),
): string {
  const a = new Date(Math.min(firstMs, lastMs));
  const b = new Date(Math.max(firstMs, lastMs));
  const sameYear = a.getFullYear() === b.getFullYear();

  if (sameYear && a.getMonth() === b.getMonth()) {
    const m = a.toLocaleDateString(locale, { month: 'long' });
    return `${m.charAt(0).toUpperCase()}${m.slice(1)} ${a.getFullYear()}`;
  }
  const isSummerMonth = (d: Date) => d.getMonth() >= 5 && d.getMonth() <= 7; // jun–aug
  if (sameYear && isSummerMonth(a) && isSummerMonth(b) && summerLabel) {
    return summerLabel;
  }
  const range = formatVisitDateRange(a.getTime(), b.getTime(), locale);
  return sameYear ? `${range} ${a.getFullYear()}` : range;
}

/**
 * Finner den SISTE sammenhengende reiseperioden (reisefilm-default): den
 * nyeste klyngen av besøk der gapet mellom påfølgende opphold er ≤ maxGapDays.
 * Walker bakover fra det seneste besøket. Returnerer {fromMs, toMs} (hele
 * dager) eller null for tom liste. Pure/testbar.
 */
export function lastContiguousPeriod(
  visits: SpotVisit[],
  maxGapDays = 3,
): { fromMs: number; toMs: number } | null {
  if (!visits.length) return null;
  const gapMs = maxGapDays * 86_400_000;
  const items = visits
    .map(v => ({
      start: v.visitedAt,
      end: typeof v.checkedOutAt === 'number' && v.checkedOutAt >= v.visitedAt ? v.checkedOutAt : v.visitedAt,
    }))
    .sort((a, b) => a.start - b.start);

  let fromMs = items[items.length - 1].start;
  let toMs = items[items.length - 1].end;
  for (let i = items.length - 2; i >= 0; i--) {
    if (fromMs - items[i].end <= gapMs) {
      fromMs = Math.min(fromMs, items[i].start);
      toMs = Math.max(toMs, items[i].end);
    } else {
      break;
    }
  }
  // Klem til hele dager (lokal tid) så datofeltene treffer.
  const dayStart = (ms: number) => { const d = new Date(ms); d.setHours(0, 0, 0, 0); return d.getTime(); };
  const dayEnd = (ms: number) => { const d = new Date(ms); d.setHours(23, 59, 59, 999); return d.getTime(); };
  return { fromMs: dayStart(fromMs), toMs: dayEnd(toMs) };
}

/**
 * Filtrer besøk til en periode (reise-replay/datofilter, eng-review D6).
 * Åpne grenser: undefined = ingen grense den veien; begge undefined = alle.
 * Et besøk regnes med hvis OPPHOLDET overlapper perioden — dvs. intervallet
 * [visitedAt, checkedOutAt ?? visitedAt] skjærer [fromMs, toMs]. Dermed telles
 * et opphold som STARTET før perioden men sjekket ut inne i den.
 * Korrupt checkedOutAt (< visitedAt) behandles som én-dags-besøk.
 * Ugyldig periode (from > to) gir tom liste — eksplisitt, ikke stille bytte.
 */
export function filterVisitsByPeriod(
  visits: SpotVisit[],
  fromMs?: number,
  toMs?: number,
): SpotVisit[] {
  if (fromMs == null && toMs == null) return visits;
  if (fromMs != null && toMs != null && fromMs > toMs) return [];
  return visits.filter(v => {
    const start = v.visitedAt;
    const end = typeof v.checkedOutAt === 'number' && v.checkedOutAt >= v.visitedAt
      ? v.checkedOutAt
      : v.visitedAt;
    if (fromMs != null && end < fromMs) return false;
    if (toMs != null && start > toMs) return false;
    return true;
  });
}

/**
 * Datointervall for et opphold (replay-kort + liste, eng-review D6):
 *   én dato («12. juli») uten checkedOutAt / samme dag / korrupt checkedOutAt,
 *   «12.–14. juli» innen samme måned (en-US: «July 12–14»),
 *   «28. juli – 2. august» på tvers av måneder,
 *   «30. desember 2025 – 2. januar 2026» på tvers av år.
 * Locale kan injiseres for deterministiske tester.
 */
export function formatVisitDateRange(
  visitedAt: number,
  checkedOutAt?: number,
  locale: string = getDateLocale(),
): string {
  const a = new Date(visitedAt);
  const validOut = typeof checkedOutAt === 'number' && checkedOutAt >= visitedAt;
  const b = validOut ? new Date(checkedOutAt as number) : a;

  const sameYear = a.getFullYear() === b.getFullYear();
  const sameMonth = sameYear && a.getMonth() === b.getMonth();
  const sameDay = sameMonth && a.getDate() === b.getDate();

  const dayMonth: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' };
  const dayMonthYear: Intl.DateTimeFormatOptions = { ...dayMonth, year: 'numeric' };
  const fmt = (d: Date, opts: Intl.DateTimeFormatOptions) => d.toLocaleDateString(locale, opts);

  if (sameDay) return fmt(a, dayMonth);
  if (sameMonth) {
    // Kollapset form: dag-først-locales «12.–14. juli», måned-først (en) «July 12–14».
    return locale.startsWith('en')
      ? `${fmt(a, dayMonth)}–${b.getDate()}`
      : `${a.getDate()}.–${fmt(b, dayMonth)}`;
  }
  if (sameYear) return `${fmt(a, dayMonth)} – ${fmt(b, dayMonth)}`;
  return `${fmt(a, dayMonthYear)} – ${fmt(b, dayMonthYear)}`;
}

/** Gruppér besøk per fylke. Besøk uten county havner under 'Ukjent fylke'. */
export function groupVisitsByCounty(visits: SpotVisit[]): Map<string, SpotVisit[]> {
  const groups = new Map<string, SpotVisit[]>();
  for (const v of visits) {
    const key = v.county || 'Ukjent fylke';
    const list = groups.get(key) || [];
    list.push(v);
    groups.set(key, list);
  }
  return groups;
}

/** Samme bildeprioritet som minikortene på kartet (MapMiniCards). */
function spotThumbnailUrl(spot: CampingSpot): string | undefined {
  return spot.ugcThumbnailUrl || spot.photoUrls?.[0] || spot.auto_image_url || undefined;
}

/**
 * Berik eksisterende besøk som mangler county/photoUrl med data fra spots-arrayet.
 * Returnerer oppdatert liste + om noe ble endret (for å vite om Firestore bør oppdateres).
 *
 * County og photoUrl berikes UAVHENGIG av hverandre. Tidligere returnerte funksjonen
 * tidlig på `if (v.county)`, så et besøk som fikk fylke først (f.eks. via reverse
 * geocoding) aldri kunne få bilde senere — fylkeskortene i profilen sto fast på
 * fallback-pin selv etter at plassen fikk bilde.
 */
export function enrichVisitsWithSpotData(
  visits: SpotVisit[],
  spots: CampingSpot[]
): { enriched: SpotVisit[]; changed: boolean } {
  const spotMap = new Map(spots.map(s => [s.id, s]));
  let changed = false;
  const enriched = visits.map(v => {
    const spot = spotMap.get(v.spotId);
    if (!spot) return v;
    const updates: Partial<SpotVisit> = {};
    if (!v.county && spot.county) updates.county = spot.county;
    if (!v.photoUrl) {
      const thumb = spotThumbnailUrl(spot);
      if (thumb) updates.photoUrl = thumb;
    }
    if (Object.keys(updates).length === 0) return v;
    changed = true;
    return { ...v, ...updates };
  });
  return { enriched, changed };
}

/** Persist berikede besøk tilbake til Firestore. */
export async function saveEnrichedVisits(userId: string, visits: SpotVisit[]): Promise<void> {
  const ref = doc(db, 'user_visits', userId);
  await setDoc(ref, { visits });
}
